import { MAX_PREVIEW_FILE_SIZE_BYTES } from '../../../app/constants/filePreview';
import { pdfPreviewService } from 'services/fileService/pdfPreviewService';
import { revokeBlobUrls } from 'shared/utils/blobUrl';

const PDF_PAGE_COUNT_SCAN_BYTES = 65536;
const MAX_REASONABLE_PAGE_COUNT = 9999;

function extractPdfPageCountFromBytes(buffer: ArrayBuffer): number | null {
  const text = new TextDecoder('latin1').decode(buffer);
  // Heuristic only: /Count can appear in page tree, outlines, or annotations.
  // We take the max reasonable match as an upload-time hint, not an exact count.
  const matches = [...text.matchAll(/\/Count\s+(\d+)/g)];
  if (matches.length === 0) {
    return null;
  }

  let maxCount = 0;
  for (const match of matches) {
    const count = parseInt(match[1], 10);
    if (
      Number.isFinite(count) &&
      count > maxCount &&
      count <= MAX_REASONABLE_PAGE_COUNT
    ) {
      maxCount = count;
    }
  }

  return maxCount > 0 ? maxCount : null;
}

function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
        return;
      }

      reject(new Error('FileReader did not return ArrayBuffer'));
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error('FileReader failed'));
    };

    reader.readAsArrayBuffer(blob);
  });
}

async function readBlobRange(
  blob: Blob,
  start: number,
  end: number
): Promise<ArrayBuffer> {
  const clampedEnd = Math.min(end, blob.size);
  return readBlobAsArrayBuffer(blob.slice(start, clampedEnd));
}

async function getPdfPageCountFromBlobLightweight(
  blob: Blob
): Promise<number | null> {
  if (blob.size === 0 || blob.size > MAX_PREVIEW_FILE_SIZE_BYTES) {
    return null;
  }

  const scanSize = Math.min(PDF_PAGE_COUNT_SCAN_BYTES, blob.size);
  const headCount = extractPdfPageCountFromBytes(
    await readBlobRange(blob, 0, scanSize)
  );
  if (headCount !== null) {
    return headCount;
  }

  if (blob.size <= scanSize) {
    return null;
  }

  return extractPdfPageCountFromBytes(
    await readBlobRange(blob, blob.size - scanSize, blob.size)
  );
}

async function getPdfPageCountFromBlobWithPdfJs(
  source: Blob
): Promise<number | undefined> {
  const blobUrl = URL.createObjectURL(source);

  try {
    return await pdfPreviewService.getPageCountFromSrc(blobUrl);
  } finally {
    revokeBlobUrls([blobUrl]);
  }
}

/** Page-count scan at upload time only (local file — heuristic, then pdf.js). */
export async function getPdfPageCountFromSource(
  source: File | Blob
): Promise<number | undefined> {
  const lightweightCount = await getPdfPageCountFromBlobLightweight(source);
  if (lightweightCount !== null && lightweightCount > 0) {
    return lightweightCount;
  }

  return getPdfPageCountFromBlobWithPdfJs(source);
}
